import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { canOpenReconcile } from '@promanage/core';
import { useQuery } from '@tanstack/react-query';
import { Panel, Select } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { ReconcileNav } from '../features/reconcile/ReconcileNav';
import { requireAccess } from '../features/auth/routeAccess';
import { ReviewQueue } from '../features/reconcile/ReviewQueue';
import { StagedBatch } from '../features/reconcile/StagedBatch';
import { HeldPayments } from '../features/reconcile/HeldPayments';
import { trpc } from '../lib/trpc';
import { queueSearch } from '../features/reconcile/queueSearch';

export const Route = createFileRoute('/reconcile/queue')({
  beforeLoad: requireAccess(canOpenReconcile),
  validateSearch: queueSearch,
  component: QueuePage,
});

/**
 * The review queue — where the statement meets the books.
 *
 * ITS OWN SCREEN, not a panel on `/reconcile`. The two jobs look adjacent and are not: ticking
 * off a day is a claim about what the bank has SHOWN, made against a statement, one day at a
 * time. Working the queue is a claim about whose money each line IS, made against the portfolio,
 * and it runs across days. Sharing a page would mean sharing a date cursor, and a queue bounded
 * by the day being reconciled would hide the very lines that are late.
 *
 * ONE ACCOUNT AT A TIME, for the reason §2 gives: each bank account reconciles separately
 * against its own statement, so its lines match against its own transactions. The picker appears
 * only when there is a choice, which for nearly every agency there is not.
 */
function QueuePage() {
  const accounts = useQuery(
    trpc.reconciliation.accounts.queryOptions(undefined, { staleTime: 10_000 }),
  );

  // Parsed again for the reason the dashboard gives: `useSearch` returns what was in the URL, not
  // what `validateSearch` kept.
  const search = queueSearch(Route.useSearch());

  // A link from the dashboard names the account its line arrived on; otherwise the first one.
  const [accountId, setAccountId] = useState<string | null>(search.account ?? null);
  const list = accounts.data?.accounts;
  const account = list?.find((option) => option.id === accountId) ?? list?.[0];

  const queue = useQuery(
    trpc.matching.queue.queryOptions(
      { ledgerAccountId: account?.id ?? '' },
      // Shorter than the default: the queue is worked by two people at once at month end, and a
      // line matched by one of them must stop being offered to the other quickly.
      { staleTime: 5_000, enabled: Boolean(account) },
    ),
  );

  const staged = useQuery(
    trpc.matching.staged.queryOptions(
      { ledgerAccountId: account?.id ?? '' },
      // As short as the queue's: two people working one statement at month end must not both
      // process the same batch, and the second press should find it already gone.
      { staleTime: 5_000, enabled: Boolean(account) },
    ),
  );

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Review queue"
        subtitle="What the bank showed, and whose money it is"
        actions={
          <>
            {list && list.length > 1 ? (
              <label className="flex flex-wrap items-center gap-2 text-[0.75rem] text-ink-muted">
                Account
                <Select
                  value={account?.id ?? ''}
                  onChange={(event) => setAccountId(event.target.value)}
                  className="w-auto px-2 py-1 text-[0.8125rem]"
                >
                  {list.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </Select>
              </label>
            ) : null}
          </>
        }
      />
      <ReconcileNav accountId={account?.id} />

      {account ? (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          <ReviewQueue
            /*
             * Keyed by the account, so switching accounts starts the cursor at the top of the
             * new queue rather than at whatever position the last one was left at — a position
             * that means nothing against a different list.
             */
            key={account.id}
            ledgerAccountId={account.id}
            accountName={account.name}
            queue={queue.data}
            isPending={queue.isPending}
            {...(search.line !== undefined && account.id === search.account
              ? { initialLineId: search.line }
              : {})}
          />

          <div className="flex min-w-0 flex-col gap-4">
            <StagedBatch
              accountName={account.name}
              ledgerAccountId={account.id}
              rows={staged.data}
              isPending={staged.isPending}
            />

            {/*
             * Beside the record rather than inside the queue. These are movements an operator
             * needs to be able to SEE on the screen they reconcile on — money leaving a trust
             * account is the half with the most regulatory weight — without them being work
             * anybody can do yet.
             */}
            <HeldPayments
              accountName={account.name}
              payments={queue.data?.payments}
              isPending={queue.isPending}
            />
          </div>
        </div>
      ) : (
        <Panel className="p-4">
          <p className="text-[0.8125rem] text-ink-muted">
            {accounts.isPending
              ? 'Loading…'
              : 'No trust bank account yet. Import a statement once one exists.'}
          </p>
        </Panel>
      )}
    </div>
  );
}
